import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, CheckCircle, Loader2, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AccessRequestFormProps {
  onBack: () => void;
  defaultType?: 'student' | 'faculty';
}

// Students must apply with a Kasr Al Ainy university email (any subdomain allowed).
const KASR_EMAIL_REGEX = /@([a-z0-9-]+\.)*kasralainy\.edu\.eg$/i;
const KASR_EMAIL_MESSAGE = 'Students must apply with their Kasr Al Ainy university email (for example name@students.kasralainy.edu.eg). Gmail, Yahoo and other addresses are not accepted.';

export function AccessRequestForm({ onBack, defaultType = 'student' }: AccessRequestFormProps) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [requestType, setRequestType] = useState<'student' | 'faculty'>(defaultType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [emailDomainError, setEmailDomainError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!fullName.trim() || !email.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Students must use a Kasr Al Ainy university email
    if (requestType === 'student' && !KASR_EMAIL_REGEX.test(email.trim())) {
      setEmailDomainError(true);
      toast.error('Students must apply with their Kasr Al Ainy university email');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('access_requests')
        .insert({
          full_name: fullName.trim(),
          email: email.trim().toLowerCase(),
          job_title: jobTitle.trim() || null,
          request_type: requestType,
          status: 'pending',
        });

      if (error) {
        // Handle duplicate pending request
        if (error.code === '23505') {
          toast.error('A pending request with this email already exists');
          return;
        }
        // Database constraint: student requests must use a Kasr Al Ainy email
        if (error.code === '23514') {
          setEmailDomainError(true);
          toast.error('Students must apply with their Kasr Al Ainy university email');
          return;
        }
        throw error;
      }

      // Kasr Al Ainy students are approved automatically; the server re-checks
      // everything. If this fails, the request simply waits for an admin.
      if (requestType === 'student') {
        try {
          const { data } = await supabase.functions.invoke('provision-user', {
            body: { action: 'auto-approve-request', email: email.trim().toLowerCase() },
          });
          if (data?.approved) {
            toast.success('Approved! Check your university email for your sign-in link.');
          }
        } catch (e) {
          console.warn('Auto-approval skipped:', e);
        }
      }

      setIsSuccess(true);
    } catch (error: any) {
      console.error('Error submitting access request:', error);
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold">Request Submitted</h3>
            <p className="text-muted-foreground">
              Your access request has been submitted successfully. You'll receive an email when your account is approved.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              If you don't receive the email within a few minutes, please check your Spam or Junk folder and mark it as "Not Spam".
            </p>
          </div>
          <Button onClick={onBack} variant="outline" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle>Request Access</CardTitle>
        <CardDescription>
          Fill out the form below to request access to KALM Hub
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name *</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailDomainError(false);
              }}
              placeholder="Enter your email"
              required
            />
          </div>

          {requestType === 'student' && emailDomainError && (
            <p className="text-sm text-destructive" role="alert">
              {KASR_EMAIL_MESSAGE}
            </p>
          )}
          {requestType === 'student' && !emailDomainError && (
            <p className="text-xs text-muted-foreground">
              Use your Kasr Al Ainy university email.
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="jobTitle">Job Title / Year (Optional)</Label>
            <Input
              id="jobTitle"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g., Year 3 Student, Resident, etc."
            />
          </div>

          <div className="space-y-3">
            <Label>I am a:</Label>
            <RadioGroup
              value={requestType}
              onValueChange={(value) => setRequestType(value as 'student' | 'faculty')}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="student" id="student" />
                <Label htmlFor="student" className="font-normal cursor-pointer">
                  Student
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="faculty" id="faculty" />
                <Label htmlFor="faculty" className="font-normal cursor-pointer">
                  Faculty / Staff
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Submit Request
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={onBack}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Login
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
